import { getDeviceTimeZone } from '@electron/shared/device-timezone';
import { format } from 'date-fns/format';

import { cn } from '@/lib/utils';
import { formatDateWithDay, formatTime } from '@/utils/date';

import {
  deviceZoneLabelIfDifferent,
  type SnapshotBlock,
  type SnapshotBlockKind,
  type SnapshotColumn,
} from '../internals/calendarSnapshot';
import { CardTime } from '../internals/CardTime';
import { isDateOnly } from '../internals/dateValue';
import { useCalendarSnapshot } from '../internals/useCalendarSnapshot';
import { useTimeFormat } from '../internals/useTimeFormat';
import { sizingClass, TEXT_STYLE } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

const HOUR_HEIGHT_REM = 2.75;
const MAX_GRID_HEIGHT_REM = 15;
const TWO_LINE_MIN_HEIGHT_REM = 2.5;

const GUTTER_CLASS = 'w-[calc(44*var(--ac-u))] shrink-0';
const SMALL_TEXT_CLASS =
  'text-[length:calc(11*var(--ac-u))] leading-none text-foreground-muted';

const BLOCK_CLASSES: Record<SnapshotBlockKind, string> = {
  proposed:
    'z-10 border border-dashed border-[color-mix(in_srgb,var(--shell-accent)_70%,transparent)] bg-[color-mix(in_srgb,var(--shell-accent)_18%,var(--surface-default))] text-[color-mix(in_srgb,var(--shell-accent)_55%,var(--foreground-default))] dark:bg-[color-mix(in_srgb,var(--shell-accent)_32%,var(--surface-default))] dark:text-[color-mix(in_srgb,var(--shell-accent)_25%,var(--foreground-default))]',
  previous:
    'bg-gray-2 text-foreground-muted ring-1 ring-inset ring-grayA-4 opacity-80 dark:bg-gray-4',
  busy: 'bg-gray-3 text-foreground-muted dark:bg-gray-5',
  duplicate: 'bg-gray-3 text-foreground-muted dark:bg-gray-5',
  free: 'text-foreground-muted ring-1 ring-inset ring-grayA-5',
  tentative:
    'bg-gray-1 bg-[repeating-linear-gradient(-45deg,var(--gray-a3)_0_0.25rem,transparent_0.25rem_0.5rem)] text-foreground-muted ring-1 ring-inset ring-grayA-4 dark:bg-gray-3',
  conflict: 'bg-warning-3 text-warning-11',
};

const BlockView = ({
  block,
  gridHeightRem,
}: {
  block: SnapshotBlock;
  gridHeightRem: number;
}) => {
  const laneWidthPercent = 100 / block.laneCount;
  const twoLines =
    (block.heightPercent / 100) * gridHeightRem >= TWO_LINE_MIN_HEIGHT_REM;
  return (
    <span
      title={block.title}
      data-kind={block.kind}
      className={cn(
        'absolute flex min-h-4 overflow-hidden rounded-md px-1.5',
        TEXT_STYLE.meta,
        'transition-[top,height,left,width] duration-200 ease-out motion-reduce:transition-none',
        twoLines ? 'flex-col py-1' : 'items-center',
        BLOCK_CLASSES[block.kind],
        block.isPast && 'opacity-50'
      )}
      style={{
        top: `calc(${block.topPercent}% + 1px)`,
        height: `calc(${block.heightPercent}% - 2px)`,
        left: `${block.lane * laneWidthPercent}%`,
        width: `calc(${laneWidthPercent}% - 3px)`,
      }}
    >
      <span
        className={cn(
          'truncate font-medium',
          block.kind === 'previous' && 'line-through'
        )}
      >
        {block.title}
      </span>
      {twoLines ? (
        <span className="truncate text-[length:calc(11*var(--ac-u))] lowercase leading-[calc(14*var(--ac-u))] opacity-75">
          <CardTime date={block.start} />
        </span>
      ) : null}
    </span>
  );
};

const ColumnGrid = ({
  column,
  markTops,
  gridHeightRem,
}: {
  column: SnapshotColumn;
  markTops: string[];
  gridHeightRem: number;
}) => (
  <span
    className="relative block min-w-0 flex-1"
    style={{ height: `${gridHeightRem}rem` }}
  >
    {markTops.map((top) => (
      <span
        key={top}
        aria-hidden="true"
        className="absolute inset-x-0 h-px bg-grayA-3"
        style={{ top }}
      />
    ))}
    {column.blocks.map((block) => (
      <BlockView key={block.id} block={block} gridHeightRem={gridHeightRem} />
    ))}
    {column.nowPercent !== null ? (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 z-20 flex -translate-y-1/2 items-center"
        style={{ top: `${column.nowPercent}%` }}
      >
        <span className="-ml-1 size-2 shrink-0 rounded-full bg-danger-9" />
        <span className="h-px flex-1 bg-danger-9" />
      </span>
    ) : null}
  </span>
);

// A Google Calendar-style view of the hours around the proposed slot: the user's
// events as grey blocks, the slot dashed in the accent, and the event's current slot
// struck, in its own day column when it falls on another day
export const CalendarSnapshot: ComponentFn<
  typeof catalog,
  'CalendarSnapshot'
> = ({ props }) => {
  // Subscribes to the Time Format setting that `formatTime` reads for the hour labels
  useTimeFormat();
  const allDay = props.allDay ?? isDateOnly(props.start);
  const snapshot = useCalendarSnapshot({
    start: props.start,
    end: props.end,
    title: props.title,
    previousStart: props.previousStart,
    previousEnd: props.previousEnd,
    replacesEventId: props.replacesEventId,
    allDay,
  });
  if (!snapshot) return null;

  const { hourMarks, columns } = snapshot;
  const visibleHours = hourMarks.length - 1;
  const gridHeightRem = Math.min(
    visibleHours * HOUR_HEIGHT_REM,
    MAX_GRID_HEIGHT_REM
  );
  const markTops = hourMarks.map(
    (_mark, index) => `${(index / visibleHours) * 100}%`
  );
  const twoColumns = columns.length > 1;
  const zoneLabel = deviceZoneLabelIfDifferent(
    new Date(props.start),
    props.timezone,
    getDeviceTimeZone()
  );
  const showsAllDay = columns.some((column) => column.allDayTitles.length > 0);
  const dayNames = columns
    .map((column) => formatDateWithDay(column.day))
    .join(' and ');

  return (
    <div
      role="group"
      aria-label={`Your calendar for ${dayNames}`}
      className={cn(
        'min-w-0 overflow-hidden rounded-lg border border-border-subtle bg-[color-mix(in_srgb,var(--surface-default),white_70%)] dark:bg-[color-mix(in_srgb,var(--surface-default),white_4%)]',
        sizingClass(props.sizing)
      )}
    >
      {twoColumns || zoneLabel ? (
        <div className="flex items-end border-b border-grayA-3 pr-2">
          <span
            className={cn(
              GUTTER_CLASS,
              SMALL_TEXT_CLASS,
              'truncate py-1.5 pl-2'
            )}
          >
            {zoneLabel}
          </span>
          {twoColumns
            ? columns.map((column) => (
                <span
                  key={column.day.getTime()}
                  className={cn(
                    'min-w-0 flex-1 truncate py-1.5 pl-2 font-medium text-foreground',
                    TEXT_STYLE.meta
                  )}
                >
                  {format(column.day, 'EEE d')}
                </span>
              ))
            : null}
        </div>
      ) : null}
      {showsAllDay ? (
        <div className="flex border-b border-grayA-3 pr-2">
          <span
            className={cn(
              GUTTER_CLASS,
              SMALL_TEXT_CLASS,
              'py-1.5 pr-2 text-right lowercase'
            )}
          >
            All day
          </span>
          {columns.map((column) => (
            <span
              key={column.day.getTime()}
              className="flex min-w-0 flex-1 flex-wrap gap-1 py-1 pl-2"
            >
              {column.allDayTitles.map((allDayTitle, index) => (
                <span
                  key={`${index}-${allDayTitle}`}
                  className={cn(
                    'max-w-full truncate rounded-md bg-gray-3 px-1.5 text-foreground-muted dark:bg-gray-5',
                    TEXT_STYLE.meta
                  )}
                >
                  {allDayTitle}
                </span>
              ))}
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex py-3 pr-2">
        <span
          aria-hidden="true"
          className={cn(GUTTER_CLASS, 'relative block')}
          style={{ height: `${gridHeightRem}rem` }}
        >
          {hourMarks.map((mark, index) => (
            <span
              key={mark.getTime()}
              className={cn(
                SMALL_TEXT_CLASS,
                'absolute right-2 -translate-y-1/2 lowercase tabular-nums'
              )}
              style={{ top: markTops[index] }}
            >
              {formatTime(mark, { hourOnly: true })}
            </span>
          ))}
        </span>
        {columns.map((column) => (
          <span
            key={column.day.getTime()}
            className="flex min-w-0 flex-1 border-l border-grayA-3 pl-2"
          >
            <ColumnGrid
              column={column}
              markTops={markTops}
              gridHeightRem={gridHeightRem}
            />
          </span>
        ))}
      </div>
    </div>
  );
};
